import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Stat, Card } from "@/components/UI";
import { Warning, Pill, Drop, Syringe, Stethoscope, Coin, TrendUp, TrendDown, Receipt, ForkKnife, FirstAid, ShoppingCart } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, CartesianGrid, Legend } from "recharts";
import { useAuth } from "@/context/AuthContext";

const CHART = ["#417251", "#C45B45", "#DDA74F", "#5A6E5F", "#8F9E93"];

export default function Dashboard() {
  const { user } = useAuth();
  const kpi = user?.kpis || {};
  const showKPI = (k) => kpi[k] !== false; // Varsayılan true — sadece açıkça false ise gizle
  const [data, setData] = useState(null);
  const [milk, setMilk] = useState(null);
  const [fin, setFin] = useState(null);
  const [reminders, setReminders] = useState(null);
  const [rationReminders, setRationReminders] = useState(null);
  const [treatReminders, setTreatReminders] = useState(null);
  const [payables, setPayables] = useState(null);
  const [cashFlow, setCashFlow] = useState([]);
  const [semenSummary, setSemenSummary] = useState(null);
  const [stockForecast, setStockForecast] = useState(null);
  const [vaccineReminders, setVaccineReminders] = useState(null);

  useEffect(() => {
    const nop = () => {};
    api.get("/dashboard").then(r => setData(r.data)).catch(nop);
    api.get("/milk/summary?days=14").then(r => setMilk(r.data)).catch(nop);
    api.get("/finance/summary").then(r => setFin(r.data)).catch(nop);
    api.get("/stock/reminders").then(r => setReminders(r.data)).catch(nop);
    api.get("/rations/reminders").then(r => setRationReminders(r.data)).catch(nop);
    api.get("/vet/treatments/reminders").then(r => setTreatReminders(r.data)).catch(nop);
    api.get("/payables/summary").then(r => setPayables(r.data)).catch(nop);
    api.get("/finance/cash-flow?months=6").then(r => setCashFlow(r.data)).catch(nop);
    api.get("/semen-stock/summary?months=6").then(r => setSemenSummary(r.data)).catch(nop);
    api.get("/stock/consumption-forecast?days=14").then(r => setStockForecast(r.data)).catch(nop);
    api.get("/vet/vaccines/reminders").then(r => setVaccineReminders(r.data)).catch(nop);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only fetch; api is a stable module import
  }, []);

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="İşletme Paneli" subtitle="Veteriner, süt ve finansal göstergeler tek bakışta." testId="dashboard-header" />

      {showKPI("kpi_stock_reminders") && reminders && reminders.count > 0 && (
        <div className="mb-6 border-2 border-[#C45B45] bg-[#fbeee9] rounded-md p-4" data-testid="reminder-banner">
          <div className="flex items-start gap-3">
            <Warning size={22} weight="fill" color="#C45B45" className="mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-[#8a3a2b] mb-1 space-y-0.5">
                {reminders.overdue_count > 0 && <div>⏰ <b>{reminders.overdue_count}</b> kalemin sayımı gecikti.</div>}
                {reminders.count - reminders.overdue_count > 0 && <div>📅 <b>{reminders.count - reminders.overdue_count}</b> kalemin sayım vakti yaklaştı.</div>}
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {reminders.items.slice(0, 6).map(x => (
                  <a key={x.id} href="/stok" className="inline-flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-md border border-[#f2d4cb] text-sm hover:bg-[#f7e6e0]" data-testid={`reminder-item-${x.name.replace(/\s/g, '-')}`}>
                    <b>{x.name}</b>
                    <span className="text-xs px-1.5 py-0.5 rounded-full"
                      style={{ background: x.reminder_status === "gecikti" ? "#C45B45" : x.reminder_status === "hiç sayım yok" ? "#8a938c" : "#DDA74F", color: "#fff" }}>
                      {x.reminder_status === "gecikti" ? `${Math.abs(x.days_until_due)} gün gecikmiş` :
                        x.reminder_status === "hiç sayım yok" ? "hiç sayım yok" : `${x.days_until_due} gün kaldı`}
                    </span>
                  </a>
                ))}
              </div>
              <a href="/stok" className="text-xs text-[#8a3a2b] underline mt-2 inline-block">Tüm sayım listesine git →</a>
            </div>
          </div>
        </div>
      )}

      {/* SPERMA SİPARİŞİ UYARISI — kritik seviyeye düşen boğalar */}
      {semenSummary?.warning_flag && semenSummary.low_stock_bulls?.length > 0 && (
        <div className="mb-6 border-2 border-[#8B2E1D] bg-[#FBECEA] rounded-md p-4" data-testid="semen-order-banner">
          <div className="flex items-start gap-3">
            <ShoppingCart size={22} weight="fill" color="#8B2E1D" className="mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[#8B2E1D] mb-1">
                🧬 Sperma Siparişi Verilmeli — <b>{semenSummary.low_stock_bulls.length}</b> boğa kritik seviyenin altında
              </div>
              <div className="flex flex-wrap gap-2 mt-2" data-testid="semen-order-bulls">
                {semenSummary.low_stock_bulls.slice(0, 8).map(b => (
                  <a key={b.id} href="/ilaclar"
                     className="inline-flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-md border border-[#E9B5AB] text-sm hover:bg-[#f7dfd9]"
                     data-testid={`semen-order-bull-${b.id}`}>
                    <b>{b.bull_name}</b>
                    <span className="text-xs tabnum text-[#8B2E1D] font-semibold">
                      {b.payet_count}/{b.min_stock}
                    </span>
                    <span className="text-[10px] text-[#8a938c]">({b.semen_type})</span>
                  </a>
                ))}
                {semenSummary.low_stock_bulls.length > 8 && (
                  <span className="text-xs text-[#8B2E1D] self-center">+{semenSummary.low_stock_bulls.length - 8} daha</span>
                )}
              </div>
              <div className="flex items-center gap-4 mt-2 text-xs text-[#8B2E1D]">
                <span>📊 Aylık ort. tüketim: <b>{semenSummary.monthly_avg_consumption} payet</b></span>
                {semenSummary.months_of_stock != null && (
                  <span>⏳ Mevcut stok yaklaşık <b>{semenSummary.months_of_stock} ay</b> yeter</span>
                )}
                <a href="/ilaclar" className="ml-auto underline">Sperma stoğuna git →</a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* KRİTİK STOK & SİPARİŞ HATIRLATICISI — Tedarik süresi + 2 gün emniyet payı */}
      {stockForecast?.alerts && stockForecast.alerts.length > 0 && (
        <div className="mb-6 border-2 border-[#C45B45] bg-[#FBECEA] rounded-md p-4" data-testid="stock-reorder-banner">
          <div className="flex items-start gap-3">
            <ShoppingCart size={22} weight="fill" color="#C45B45" className="mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[#8B2E1D] mb-1">
                🛒 Kritik Stok — Sipariş Hatırlatıcısı ({stockForecast.alerts.length} kalem)
              </div>
              <div className="text-xs text-[#8B2E1D] mb-2">
                Günlük ortalama tüketim hızına göre stok, tedarik süresi + 2 gün emniyet payının altına düştü.
              </div>
              <div className="grid md:grid-cols-2 gap-2" data-testid="stock-reorder-banner-list">
                {stockForecast.alerts.slice(0, 6).map(a => (
                  <a key={a.id} href="/stok"
                     className="bg-white border border-[#E9B5AB] px-3 py-2 rounded flex items-start justify-between gap-2 text-sm hover:bg-[#f7dfd9]"
                     data-testid={`stock-reorder-item-${a.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[#8B2E1D] truncate">{a.name}</div>
                      <div className="text-xs text-[#5e675e] tabnum">
                        {a.quantity_kg != null ? `${a.quantity_kg} kg` : `${a.quantity} ${a.unit}`}
                        {a.daily_avg_kg > 0 && ` · ${a.daily_avg_kg} kg/gün`}
                      </div>
                    </div>
                    {a.days_remaining != null && (
                      <span className="text-xs bg-[#C45B45] text-white px-2 py-0.5 rounded font-semibold self-center whitespace-nowrap">
                        {a.days_remaining}g
                      </span>
                    )}
                  </a>
                ))}
              </div>
              {stockForecast.alerts.length > 6 && (
                <div className="text-xs text-[#8B2E1D] mt-1">+{stockForecast.alerts.length - 6} daha</div>
              )}
              <a href="/stok" className="text-xs text-[#8B2E1D] underline mt-2 inline-block">Stok yönetimine git →</a>
            </div>
          </div>
        </div>
      )}

      {/* SON KULLANMA TARİHİ (SKT) UYARISI — süresi geçmiş / yaklaşan ilaçlar */}
      {data?.expiring_medicines?.length > 0 && (
        <div className="mb-6 border-2 border-[#C45B45] bg-[#FBECEA] rounded-md p-4" data-testid="med-expiry-banner">
          <div className="flex items-start gap-3">
            <FirstAid size={22} weight="fill" color="#C45B45" className="mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[#8B2E1D] mb-1">
                💊 Son Kullanma Tarihi Uyarısı ({data.expiring_medicines.length} ilaç)
              </div>
              <div className="text-xs text-[#8B2E1D] mb-2">
                Süresi geçen (kırmızı) veya 60 gün içinde dolacak ilaçlar. Kullanmadan önce kontrol edin.
              </div>
              <div className="grid md:grid-cols-2 gap-2" data-testid="med-expiry-banner-list">
                {data.expiring_medicines.slice(0, 6).map(m => (
                  <a key={m.id} href="/ilaclar"
                     className="bg-white border border-[#E9B5AB] px-3 py-2 rounded flex items-start justify-between gap-2 text-sm hover:bg-[#f7dfd9]"
                     data-testid={`med-expiry-item-${m.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[#8B2E1D] truncate">{m.name}</div>
                      <div className="text-xs text-[#5e675e] tabnum">SKT: {m.expiry_date} · {m.stock} {m.unit}</div>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold self-center whitespace-nowrap ${m.expired ? "bg-[#C45B45] text-white" : "bg-[#EED7A6] text-[#8B6914]"}`}>
                      {m.expired ? `${Math.abs(m.days_until_expiry)}g geçti` : `${m.days_until_expiry}g`}
                    </span>
                  </a>
                ))}
              </div>
              {data.expiring_medicines.length > 6 && (
                <div className="text-xs text-[#8B2E1D] mt-1">+{data.expiring_medicines.length - 6} daha</div>
              )}
              <a href="/ilaclar" className="text-xs text-[#8B2E1D] underline mt-2 inline-block">İlaç yönetimine git →</a>
            </div>
          </div>
        </div>
      )}


      {/* AŞI TAKVİMİ UYARISI — 2 gün kala + gecikmiş rapel/periyodik görevler */}
      {vaccineReminders && vaccineReminders.count > 0 && (
        <div className="mb-6 border-2 border-[#417251] bg-[#eaf1ec] rounded-md p-4" data-testid="vaccine-reminder-banner">
          <div className="flex items-start gap-3">
            <Syringe size={22} weight="fill" color="#2D4A36" className="mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[#2D4A36] mb-1">
                💉 Aşı Zamanı — <b>{vaccineReminders.count}</b> rapel/periyodik uygulama yaklaşıyor veya gecikti
              </div>
              <div className="flex flex-wrap gap-2 mt-2" data-testid="vaccine-reminder-list">
                {vaccineReminders.items.slice(0, 6).map(v => (
                  <a key={v.id} href="/veteriner"
                     className="inline-flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-md border border-[#c9d5cc] text-sm hover:bg-[#e0e9e2]"
                     data-testid={`vaccine-reminder-item-${v.id}`}>
                    <b>{v.vaccine_name}</b>
                    <span className="text-xs text-[#8a938c]">{v.task_label} · Doz {v.dose_number}/{v.doses_per_cycle}</span>
                    <span className="text-xs px-1.5 py-0.5 rounded-full text-white tabnum font-semibold"
                      style={{ background: v.days_until_due < 0 ? "#C45B45" : "#DDA74F" }}>
                      {v.days_until_due < 0 ? `${Math.abs(v.days_until_due)}g gecikti` : v.days_until_due === 0 ? "Bugün" : `${v.days_until_due}g kaldı`}
                    </span>
                  </a>
                ))}
                {vaccineReminders.count > 6 && (
                  <span className="text-xs text-[#2D4A36] self-center">+{vaccineReminders.count - 6} daha</span>
                )}
              </div>
              <a href="/veteriner" className="text-xs text-[#2D4A36] underline mt-2 inline-block">Aşı takvimine git →</a>
            </div>
          </div>
        </div>
      )}

      {/* Bugünün Yapılacakları — yemlenmemiş rasyonlar + uygulanmamış tedaviler */}
      {((showKPI("kpi_ration_reminders") && rationReminders && rationReminders.total > 0) ||
        (showKPI("kpi_treatment_reminders") && treatReminders && treatReminders.count > 0)) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-testid="today-tasks">
          {showKPI("kpi_ration_reminders") && rationReminders && rationReminders.total > 0 && (
            <div className="border-2 border-[#DDA74F] bg-[#fdf5e5] rounded-md p-4" data-testid="ration-reminder-banner">
              <div className="flex items-start gap-3">
                <ForkKnife size={22} weight="fill" color="#8B6914" className="mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#7a5a10] mb-1">
                    🥣 <b>{rationReminders.total}</b> rasyon bugün henüz yemlenmedi.
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {rationReminders.not_fed.slice(0, 6).map(r => (
                      <a key={r.id} href="/rasyon" className="inline-flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-md border border-[#f0d9a3] text-sm hover:bg-[#fbf0d5]" data-testid={`ration-reminder-item-${r.id}`}>
                        <b>{r.name}</b>
                        <span className="text-xs text-[#8a938c]">· {r.animal_count} baş</span>
                      </a>
                    ))}
                    {rationReminders.total > 6 && (
                      <span className="text-xs text-[#7a5a10] self-center">+{rationReminders.total - 6} daha</span>
                    )}
                  </div>
                  <a href="/rasyon" className="text-xs text-[#7a5a10] underline mt-2 inline-block">Rasyon sayfasına git →</a>
                </div>
              </div>
            </div>
          )}

          {showKPI("kpi_treatment_reminders") && treatReminders && treatReminders.count > 0 && (
            <div className="border-2 border-[#417251] bg-[#eaf1ec] rounded-md p-4" data-testid="treat-reminder-banner">
              <div className="flex items-start gap-3">
                <FirstAid size={22} weight="fill" color="#2D4A36" className="mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#2D4A36] mb-1">
                    💊 <b>{treatReminders.count}</b> tedavi bugün henüz uygulanmadı.
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {treatReminders.pending.slice(0, 6).map(t => (
                      <a key={t.id} href="/veteriner" className="inline-flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-md border border-[#c9d5cc] text-sm hover:bg-[#e0e9e2]" data-testid={`treat-reminder-item-${t.id}`}>
                        <b>{t.diagnosis || "—"}</b>
                        {t.medicine_name && <span className="text-xs text-[#8a938c]">· {t.medicine_name}</span>}
                        {t.treatment_duration_days > 0 && (
                          <span className="text-xs px-1.5 py-0.5 rounded-full bg-[#DDA74F] text-white tabnum">
                            {t.applications_count}/{t.treatment_duration_days}
                          </span>
                        )}
                      </a>
                    ))}
                    {treatReminders.count > 6 && (
                      <span className="text-xs text-[#2D4A36] self-center">+{treatReminders.count - 6} daha</span>
                    )}
                  </div>
                  <a href="/veteriner" className="text-xs text-[#2D4A36] underline mt-2 inline-block">Veteriner sayfasına git →</a>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        {showKPI("kpi_today_milk") && <Stat testId="stat-milk" label="Bugünkü Süt" value={data?.today_milk ?? "—"} unit="L" tone="accent" />}
        {showKPI("kpi_inseminations_week") && <Stat testId="stat-week-ins" label="Haftalık Tohumlama" value={data?.week_inseminations ?? "—"} unit="adet" tone="ok" />}
        {showKPI("kpi_inseminations_month") && <Stat testId="stat-month-ins" label="Aylık Tohumlama" value={data?.month_inseminations ?? "—"} unit="adet" />}
        {showKPI("kpi_active_treatments") && <Stat testId="stat-active-treat" label="Aktif Tedavi" value={data?.active_treatments ?? "—"} unit="baş" tone="warn" />}
        {showKPI("kpi_month_revenue") && <Stat testId="stat-income" label="Bu Ay Gelir" value={(data?.month_income ?? 0).toLocaleString("tr-TR")} unit="₺" />}
        {showKPI("kpi_month_net") && <Stat testId="stat-net" label="Bu Ay Net" value={(data?.month_net ?? 0).toLocaleString("tr-TR")} unit="₺" />}
      </div>

      {/* Financial Overview Panel */}
      {showKPI("kpi_financial_panel") && payables && (payables.total_debt > 0 || payables.total_receivable > 0 || cashFlow.some(x => x.income || x.expense)) && (
        <div className="mb-6" data-testid="finance-panel">
          <div className="flex items-center gap-2 mb-3">
            <Receipt size={20} weight="duotone" className="text-[#2D4A36]" />
            <h2 className="heading text-xl">Finansal Panel</h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <Card testId="fin-panel-debt" className="!bg-[#fbeee9] border border-[#f2d4cb]">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs uppercase tracking-widest text-[#8a3a2b]">Toplam Borç</span>
                <TrendDown size={16} weight="fill" className="text-[#C45B45]" />
              </div>
              <div className="text-2xl font-bold tabnum text-[#8a3a2b]">
                {payables.total_debt.toLocaleString("tr-TR", { maximumFractionDigits: 0 })} <span className="text-sm font-normal">₺</span>
              </div>
              {payables.overdue_debt > 0 && (
                <div className="text-xs text-[#8a3a2b] mt-1">
                  ⚠️ {payables.overdue_debt.toLocaleString("tr-TR")} ₺ vadesi geçti
                </div>
              )}
            </Card>

            <Card testId="fin-panel-receivable" className="!bg-[#E4EEE6] border border-[#c2d6c8]">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs uppercase tracking-widest text-[#2D4A36]">Toplam Alacak</span>
                <TrendUp size={16} weight="fill" className="text-[#2D4A36]" />
              </div>
              <div className="text-2xl font-bold tabnum text-[#2D4A36]">
                {payables.total_receivable.toLocaleString("tr-TR", { maximumFractionDigits: 0 })} <span className="text-sm font-normal">₺</span>
              </div>
              {payables.overdue_receivable > 0 && (
                <div className="text-xs text-[#8a3a2b] mt-1">
                  ⚠️ {payables.overdue_receivable.toLocaleString("tr-TR")} ₺ tahsil edilmedi
                </div>
              )}
            </Card>

            <Card testId="fin-panel-net-position">
              <div className="text-xs uppercase tracking-widest text-[#6b746c] mb-1">Net Pozisyon</div>
              <div className={`text-2xl font-bold tabnum ${payables.net_position >= 0 ? "text-[#2D4A36]" : "text-[#C45B45]"}`}>
                {payables.net_position >= 0 ? "+" : ""}{payables.net_position.toLocaleString("tr-TR", { maximumFractionDigits: 0 })} <span className="text-sm font-normal">₺</span>
              </div>
              <div className="text-xs text-[#8a938c] mt-1">alacak − borç</div>
            </Card>

            <Card testId="fin-panel-month-net">
              <div className="text-xs uppercase tracking-widest text-[#6b746c] mb-1">Bu Ay Kâr/Zarar</div>
              <div className={`text-2xl font-bold tabnum ${(data?.month_net ?? 0) >= 0 ? "text-[#2D4A36]" : "text-[#C45B45]"}`}>
                {(data?.month_net ?? 0) >= 0 ? "+" : ""}{(data?.month_net ?? 0).toLocaleString("tr-TR", { maximumFractionDigits: 0 })} <span className="text-sm font-normal">₺</span>
              </div>
              <div className="text-xs text-[#8a938c] mt-1">gelir − gider</div>
            </Card>
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            {/* Monthly cash flow chart */}
            <Card testId="cash-flow-chart" className="lg:col-span-2">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-[#6b746c]">Son 6 ay</div>
                  <h3 className="heading text-lg">Aylık Nakit Akışı</h3>
                </div>
              </div>
              <div style={{ height: 220 }}>
                <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                  <BarChart data={cashFlow}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DC" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#8a938c" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#8a938c" }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip
                      contentStyle={{ borderRadius: 6, border: "1px solid #E5E2DC", fontSize: 12 }}
                      formatter={v => `${v.toLocaleString("tr-TR")} ₺`}
                    />
                    <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="income" fill="#2D4A36" radius={[4, 4, 0, 0]} name="Gelir" />
                    <Bar dataKey="expense" fill="#C45B45" radius={[4, 4, 0, 0]} name="Gider" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            {/* Due schedule */}
            <Card testId="due-schedule-card">
              <div className="text-[11px] uppercase tracking-widest text-[#6b746c]">Ödeme takvimi</div>
              <h3 className="heading text-lg mb-3">Vade Programı</h3>
              <div className="space-y-2">
                {payables.due_schedule.map((b, i) => {
                  const total = payables.due_schedule.reduce((s, x) => s + x.amount, 0) || 1;
                  const pct = (b.amount / total) * 100;
                  const color = i === 0 ? "#C45B45" : i === 1 ? "#DDA74F" : i === 2 ? "#417251" : "#5A6E5F";
                  return (
                    <div key={b.bucket} data-testid={`due-${i}`}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="font-semibold">{b.bucket}</span>
                        <span className="tabnum text-[#5e675e]">{b.amount.toLocaleString("tr-TR", { maximumFractionDigits: 0 })} ₺</span>
                      </div>
                      <div className="h-2 bg-[#F5F2EC] rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <a href="/finans" className="text-xs text-[#2D4A36] underline mt-3 inline-block">Ödemeler sekmesine git →</a>
            </Card>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        {/* Milk chart */}
        <Card testId="milk-chart-card" className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-[#6b746c]">Son 14 gün</div>
              <h3 className="heading text-xl">Süt Üretimi</h3>
            </div>
            <div className="flex items-center gap-2 text-sm text-[#5e675e]">
              <Drop weight="fill" color="#417251" /> Toplam: <b className="text-[#1A1D1A]">{milk?.total ?? 0} L</b>
            </div>
          </div>
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <LineChart data={milk?.series ?? []}>
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#8a938c" }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fontSize: 11, fill: "#8a938c" }} />
                <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E5E2DC" }} />
                <Line type="monotone" dataKey="liters" stroke="#2D4A36" strokeWidth={2.5} dot={{ r: 3, fill: "#2D4A36" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Expenses pie */}
        <Card testId="expense-pie-card">
          <div className="text-[11px] uppercase tracking-widest text-[#6b746c]">Kategori dağılımı</div>
          <h3 className="heading text-xl mb-4">Gider Analizi</h3>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <PieChart>
                <Pie data={fin?.expense_by_category ?? []} dataKey="amount" nameKey="category" innerRadius={45} outerRadius={80} paddingAngle={2}>
                  {(fin?.expense_by_category ?? []).map((c, i) => <Cell key={c.category} fill={CHART[i % CHART.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E5E2DC" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 space-y-1.5 text-sm">
            {(fin?.expense_by_category ?? []).slice(0, 5).map((x, i) => (
              <div key={x.category} className="flex justify-between">
                <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: CHART[i % CHART.length] }} />{x.category}</span>
                <span className="tabnum">{x.amount.toLocaleString("tr-TR")} ₺</span>
              </div>
            ))}
            {(!fin?.expense_by_category?.length) && <div className="text-xs text-[#8a938c]">Henüz kayıt yok</div>}
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card testId="low-stock-card">
          <div className="flex items-center gap-2 mb-3">
            <Warning weight="fill" color="#C45B45" />
            <h3 className="heading text-lg">Stok Uyarısı</h3>
          </div>
          {data?.low_stock_medicines?.length ? (
            <ul className="space-y-2">
              {data.low_stock_medicines.map((m) => (
                <li key={m.name} className="flex justify-between text-sm border-b border-[#EEECE5] pb-1.5">
                  <span>{m.name}</span>
                  <span className="tabnum text-[#C45B45] font-semibold">{m.stock} / {m.min_stock}</span>
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-[#8a938c]">Stok yeterli — kritik altı ilaç yok.</p>}
        </Card>

        <Card testId="top-meds-card">
          <div className="flex items-center gap-2 mb-3">
            <Pill weight="fill" color="#417251" />
            <h3 className="heading text-lg">Aylık En Çok Kullanılan İlaçlar</h3>
          </div>
          {data?.top_medicines?.length ? (
            <ul className="space-y-2">
              {data.top_medicines.map((m) => (
                <li key={m.name} className="flex justify-between text-sm border-b border-[#EEECE5] pb-1.5">
                  <span>{m.name}</span>
                  <span className="tabnum font-semibold">{m.count}x</span>
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-[#8a938c]">Henüz tedavi kaydı yok.</p>}
        </Card>
      </div>
    </div>
  );
}
